import { useState, useEffect } from 'react';
import { createClient } from '@blinkdotnew/sdk';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  User,
  Tag,
  Calendar,
  Sparkles,
  Filter
} from 'lucide-react';
import { TaskListSkeleton } from '@/components/ui/data-skeleton';
import { telemetry, telemetryHelpers } from '@/lib/telemetry';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false,
  auth: { mode: 'headless' }
});

interface Task {
  id: string;
  userId: string;
  contactId?: string;
  title: string;
  description?: string;
  dueDate?: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in_progress' | 'completed';
  source?: string;
  sourceId?: string;
  assignedBy?: string;
  tags?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

interface Contact {
  id: string;
  email: string;
  name?: string;
}

export default function TasksPage() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  useEffect(() => {
    telemetryHelpers.trackPageLoad('tasks');

    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      if (state.user) {
        setUser(state.user);
        telemetry.setUserId(state.user.id);
        // Lazy load tasks after slight delay for better UX
        setTimeout(() => loadTasks(state.user.id), 100);
      }
    });

    return () => {
      unsubscribe();
      telemetryHelpers.completePageLoad('tasks');
    };
  }, []);

  const loadTasks = async (userId: string) => {
    setLoading(true);
    try {
      await telemetryHelpers.trackDataFetch('tasks-data', async () => {
        const [tasksData, contactsData] = await Promise.all([
          telemetryHelpers.trackDataFetch('tasks', () =>
            blink.db.tasks.list({
              where: { userId },
              orderBy: { createdAt: 'desc' }
            })
          ),
          telemetryHelpers.trackDataFetch('contacts', () =>
            blink.db.contacts.list({
              where: { userId }
            })
          )
        ]);

        setTasks(tasksData);
        setContacts(contactsData);
      });
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskStatus = async (taskId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
      const now = new Date().toISOString();

      await blink.db.tasks.update(taskId, {
        status: newStatus,
        completedAt: newStatus === 'completed' ? now : null,
        updatedAt: now
      });

      // Update local state
      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, status: newStatus as any, completedAt: newStatus === 'completed' ? now : undefined }
          : task
      ));

      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  const getContactName = (contactId?: string) => {
    if (!contactId) return 'No contact';
    const contact = contacts.find(c => c.id === contactId);
    return contact?.name || contact?.email || 'Unknown';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getSourceIcon = (source?: string) => {
    if (source === 'voice_journal') return <Sparkles className="w-3 h-3 text-primary" />;
    return <Clock className="w-3 h-3 text-muted-foreground" />;
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'pending' && task.status === 'completed') return false;
    if (filter === 'completed' && task.status !== 'completed') return false;
    if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;
    return true;
  });

  const pendingTasks = tasks.filter(t => t.status !== 'completed');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const highPriorityTasks = pendingTasks.filter(t => t.priority === 'high');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl gradient-primary mx-auto animate-pulse-slow" />
          <p className="text-muted-foreground">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Tasks</h1>
        <p className="text-muted-foreground mt-2">
          AI-extracted action items and to-dos from your interactions
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Circle className="w-4 h-4 text-blue-500" />
              Pending Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingTasks.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Active tasks</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              High Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{highPriorityTasks.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Urgent tasks</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedTasks.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Done</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Tasks</CardTitle>
              <CardDescription>Manage your AI-generated tasks</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Tabs value={filter} onValueChange={(v) => setFilter(v as any)} className="w-auto">
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="pending">Pending</TabsTrigger>
                  <TabsTrigger value="completed">Done</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Priority:</span>
            <div className="flex gap-2">
              <Badge 
                variant={priorityFilter === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setPriorityFilter('all')}
              >
                All
              </Badge>
              <Badge 
                variant={priorityFilter === 'high' ? 'destructive' : 'outline'}
                className="cursor-pointer"
                onClick={() => setPriorityFilter('high')}
              >
                High
              </Badge>
              <Badge 
                variant={priorityFilter === 'medium' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setPriorityFilter('medium')}
              >
                Medium
              </Badge>
              <Badge 
                variant={priorityFilter === 'low' ? 'secondary' : 'outline'}
                className="cursor-pointer"
                onClick={() => setPriorityFilter('low')}
              >
                Low
              </Badge>
            </div>
          </div>

          {loading ? (
            <TaskListSkeleton />
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No tasks found</p>
              <p className="text-sm mt-1">
                Tasks will be automatically created from your voice notes and interactions
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-3 pr-4">
                {filteredTasks.map((task) => (
                  <Card key={task.id} className={task.status === 'completed' ? 'opacity-60' : ''}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <Checkbox
                          checked={task.status === 'completed'}
                          onCheckedChange={() => toggleTaskStatus(task.id, task.status)}
                          className="mt-1"
                        />
                        
                        <div className="flex-1 space-y-2">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className={`font-medium ${task.status === 'completed' ? 'line-through' : ''}`}>
                                {task.title}
                              </h3>
                              {task.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {task.description}
                                </p>
                              )}
                            </div>
                            <Badge variant={getPriorityColor(task.priority)}>
                              {task.priority}
                            </Badge>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            {task.contactId && (
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {getContactName(task.contactId)}
                              </div>
                            )}
                            
                            {task.dueDate && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Due: {format(new Date(task.dueDate), 'MMM d, yyyy')}
                              </div>
                            )}

                            {task.source && (
                              <div className="flex items-center gap-1">
                                {getSourceIcon(task.source)}
                                {task.assignedBy === 'ai' ? 'AI-generated' : 'Manual'}
                              </div>
                            )}

                            {task.tags && (
                              <div className="flex items-center gap-1">
                                <Tag className="w-3 h-3" />
                                {task.tags.split(',').slice(0, 2).join(', ')}
                              </div>
                            )}
                          </div>

                          {task.completedAt && (
                            <div className="text-xs text-green-600 dark:text-green-400">
                              ✓ Completed {format(new Date(task.completedAt), 'MMM d, yyyy h:mm a')}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
